import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Modal, Button } from "react-bootstrap";
import { BiSearch } from "react-icons/bi";
import { MdOutlineEdit, MdDeleteOutline, MdAdd } from "react-icons/md";
import {
  listCampaigns,
  createCampaign,
  deleteCampaign,
  activateCampaign,
  pauseCampaign,
  resumeCampaign,
} from "../../Service/advertisingApi";
import { hasPermission } from "../../Utils/permissionUtils";
import { DeletePopupModal } from "../../Components/PopupModal/DeletePopupModal";
import AlertDeletePopupModal from "../../Components/PopupModal/AlertDeletePopupModal";
import {
  CAMPAIGN_STATUSES,
  STATUS_COLORS,
  formatCurrency,
  formatDate,
  fromDatetimeLocal,
} from "./constants";
import "./advertising.css";

const tableCustomStyles = {
  cells: { style: { justifyContent: "center" } },
  headCells: {
    style: {
      justifyContent: "center",
      color: "rgb(33,37,41)",
      fontSize: "12px",
      fontWeight: 500,
    },
  },
};

const emptyForm = {
  name: "",
  advertiser_name: "",
  start_date: "",
  end_date: "",
  status: "draft",
  priority: 5,
  budget: "",
  notes: "",
  sponsor_logo: null,
};

const AdvertisingCampaigns = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [alertDeleteType, setAlertDeleteType] = useState("");
  const [deletePopupType, setDeletePopupType] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const canAdd = hasPermission("advertising.add_adcampaign");
  const canChange = hasPermission("advertising.change_adcampaign");
  const canDelete = hasPermission("advertising.delete_adcampaign");
  const canView = hasPermission("advertising.view_adcampaign");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, page_size: rowPerPage };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await listCampaigns(params);
      setData(res.data.results || []);
      setTotalRows(res.data.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowPerPage, statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name.trim() || !form.advertiser_name.trim()) {
      setFormError("Name and advertiser name are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("advertiser_name", form.advertiser_name.trim());
      payload.append("status", form.status);
      payload.append("priority", String(form.priority || 5));
      if (form.budget !== "") payload.append("budget", String(form.budget));
      if (form.notes) payload.append("notes", form.notes);
      const start = fromDatetimeLocal(form.start_date);
      const end = fromDatetimeLocal(form.end_date);
      if (start) payload.append("start_date", start);
      if (end) payload.append("end_date", end);
      if (form.sponsor_logo) payload.append("sponsor_logo", form.sponsor_logo);

      const res = await createCampaign(payload);
      setShowCreate(false);
      setForm(emptyForm);
      navigate(`/dashboard/advertising/campaigns/${res.data.id}`);
    } catch (err) {
      setFormError(err.response?.data?.detail || "Failed to create campaign.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (campaignId) => {
    try {
      await deleteCampaign(campaignId);
      setAlertDeleteType("");
      setDeleteId(null);
      setDeletePopupType("success");
      fetchData();
    } catch (err) {
      setDeletePopupType("error");
      setAlertDeleteType("");
      console.error(err);
    }
  };

  const handleStatusAction = async (id, action) => {
    setActionLoading(`${id}-${action}`);
    try {
      if (action === "activate") await activateCampaign(id);
      else if (action === "pause") await pauseCampaign(id);
      else if (action === "resume") await resumeCampaign(id);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const columns = [
    {
      name: "S.No",
      width: "70px",
      cell: (_, i) => (currentPage - 1) * rowPerPage + i + 1,
    },
    {
      name: "Campaign",
      minWidth: "180px",
      cell: (row) => (
        <div className="adv-cell-campaign">
          {row.sponsor_logo ? (
            <img src={row.sponsor_logo} alt="" className="adv-logo-thumb" />
          ) : null}
          <div>
            <div className="adv-cell-title">{row.name}</div>
            <div className="adv-cell-sub">{row.advertiser_name}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Status",
      width: "100px",
      cell: (row) => (
        <span
          className="adv-status-badge"
          style={{ background: `${STATUS_COLORS[row.status] || "#6b7280"}22`, color: STATUS_COLORS[row.status] }}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Dates",
      minWidth: "140px",
      cell: (row) => (
        <div className="adv-cell-dates">
          <span>{formatDate(row.start_date)}</span>
          <span className="adv-cell-sub">→ {formatDate(row.end_date)}</span>
        </div>
      ),
    },
    {
      name: "Priority",
      width: "80px",
      selector: (row) => row.priority,
    },
    {
      name: "Budget",
      width: "110px",
      cell: (row) => formatCurrency(row.budget),
    },
    {
      name: "Ads",
      width: "60px",
      selector: (row) => row.advertisement_count ?? 0,
    },
    {
      name: "Actions",
      minWidth: "220px",
      cell: (row) => (
        <div className="adv-actions">
          {(canView || canChange) && (
            <button
              type="button"
              className="adv-btn adv-btn--ghost"
              title="Edit"
              onClick={() => navigate(`/dashboard/advertising/campaigns/${row.id}`)}
            >
              <MdOutlineEdit size={18} />
            </button>
          )}
          {canChange && row.status === "draft" && (
            <button
              type="button"
              className="adv-btn adv-btn--success"
              disabled={actionLoading === `${row.id}-activate`}
              onClick={() => handleStatusAction(row.id, "activate")}
            >
              Activate
            </button>
          )}
          {canChange && row.status === "active" && (
            <button
              type="button"
              className="adv-btn adv-btn--warn"
              disabled={actionLoading === `${row.id}-pause`}
              onClick={() => handleStatusAction(row.id, "pause")}
            >
              Pause
            </button>
          )}
          {canChange && row.status === "paused" && (
            <button
              type="button"
              className="adv-btn adv-btn--success"
              disabled={actionLoading === `${row.id}-resume`}
              onClick={() => handleStatusAction(row.id, "resume")}
            >
              Resume
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="adv-btn adv-btn--ghost adv-btn--danger"
              title="Delete"
              onClick={() => {
                setDeleteId(row.id);
                setAlertDeleteType("success");
              }}
            >
              <MdDeleteOutline size={18} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!canView && !canAdd && !canChange) {
    return (
      <div className="advertising-page">
        <p className="adv-error">You do not have permission to view advertising campaigns.</p>
      </div>
    );
  }

  return (
    <div className="advertising-page">
      <div className="adv-toolbar">
        <h1 className="adv-title">Advertising Campaigns</h1>
        <div className="adv-toolbar-right">
          <select
            className="adv-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            {CAMPAIGN_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <form className="adv-search-form" onSubmit={handleSearch}>
            <input
              type="text"
              className="adv-input adv-search-input"
              placeholder="Search name or advertiser…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="adv-btn adv-btn--primary">
              <BiSearch size={18} />
            </button>
          </form>
          {canAdd && (
            <button type="button" className="adv-btn adv-btn--primary" onClick={() => setShowCreate(true)}>
              <MdAdd size={20} /> New Campaign
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        onChangePage={setCurrentPage}
        onChangeRowsPerPage={(size) => {
          setRowPerPage(size);
          setCurrentPage(1);
        }}
        customStyles={tableCustomStyles}
        onRowClicked={(row) => navigate(`/dashboard/advertising/campaigns/${row.id}`)}
        pointerOnHover
        highlightOnHover
      />

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>New Campaign</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreate}>
          <Modal.Body>
            {formError && <div className="adv-form-error">{formError}</div>}
            <div className="adv-form-grid">
              <label className="adv-field">
                <span>Campaign Name *</span>
                <input
                  className="adv-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="adv-field">
                <span>Advertiser Name *</span>
                <input
                  className="adv-input"
                  value={form.advertiser_name}
                  onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })}
                  required
                />
              </label>
              <label className="adv-field">
                <span>Start Date</span>
                <input
                  type="datetime-local"
                  className="adv-input"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>End Date</span>
                <input
                  type="datetime-local"
                  className="adv-input"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>Status</span>
                <select
                  className="adv-select adv-select--full"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {CAMPAIGN_STATUSES.filter((s) => s.value).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="adv-field">
                <span>Priority (1–10)</span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="adv-input"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>Budget (INR)</span>
                <input
                  type="number"
                  min={0}
                  className="adv-input"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                />
              </label>
              <label className="adv-field">
                <span>Sponsor Logo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="adv-input"
                  onChange={(e) => setForm({ ...form, sponsor_logo: e.target.files?.[0] || null })}
                />
              </label>
              <label className="adv-field adv-field--full">
                <span>Notes</span>
                <textarea
                  className="adv-textarea"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Creating…" : "Create Campaign"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {alertDeleteType === "success" && (
        <AlertDeletePopupModal
          AlertDeletePopupType={alertDeleteType}
          HandleDelete={confirmDelete}
          CancelAlerrt={() => setAlertDeleteType("")}
          row={deleteId}
        />
      )}
      {deletePopupType !== "" && (
        <DeletePopupModal
          deletePopupType={deletePopupType}
          setDeletepopupType={setDeletePopupType}
        />
      )}
    </div>
  );
};

export { AdvertisingCampaigns };
